import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFocusTrap } from "../hooks/useFocusTrap";

// Premier élément VISIBLE qui répond au sélecteur : un élément masqué (menu fermé, bouton réservé à l'ordinateur) est
// bien dans la page, mais sans boîte — l'étape le désignerait dans le vide (01/10).
const visibleMatch = (selector) => [...document.querySelectorAll(selector)].find((el) => el.getClientRects().length > 0) || null;

const CANDIDATE_STEPS = [
  {
    selector: '[data-testid="pending-tasks"]',
    title: "Vos tâches en attente",
    body: "Les actions qui nécessitent votre attention (validations, formulaires à compléter...) apparaissent ici en priorité.",
  },
  {
    selector: '[data-testid="client-quick-links"]',
    title: "Accès rapide",
    body: "Retrouvez vos raccourcis principaux directement depuis le tableau de bord, comme votre identité de marque.",
  },
  {
    selector: '[data-testid="nav-Mes Projets"], [data-testid="drawer-nav-Mes Projets"], [data-testid="nav-drawer-open"]',
    title: "Suivi de vos projets et factures",
    body: "Le détail de chaque projet — avancement, livrables et factures — est accessible depuis ce menu.",
  },
  {
    selector: '[data-testid="notif-bell"]',
    title: "Notifications",
    body: "Vous serez alerté ici dès qu'une action de votre part est nécessaire.",
  },
  {
    // Sur téléphone et tablette, l'aide est dans le menu (décision du 01/10) : l'étape désigne alors le bouton du menu.
    selector: '[data-testid="floating-help-btn"], [data-testid="nav-drawer-open"]',
    title: "Besoin d'aide ?",
    body: "Relancez cette visite guidée à tout moment, ou contactez l'agence directement depuis ici.",
  },
];

export default function GuidedTour() {
  const { user, setUser } = useAuth();
  const [steps, setSteps] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const panelRef = useRef(null);
  const reduce = useReducedMotion();

  useFocusTrap(panelRef, open, () => finish());

  const launch = () => {
    const available = CANDIDATE_STEPS.filter(function (s) {
      return visibleMatch(s.selector);
    });
    if (available.length === 0) return;
    setSteps(available);
    setStep(0);
    setOpen(true);
  };

  useEffect(() => {
    window.addEventListener("launch-guided-tour", launch);
    return () => window.removeEventListener("launch-guided-tour", launch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.role === "client" && user.onboarding && user.onboarding.completed && !user.onboarding.tour_seen) {
      const t = setTimeout(launch, 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.onboarding && user.onboarding.completed, user && user.onboarding && user.onboarding.tour_seen, user && user.role]);

  useEffect(() => {
    if (!open || !steps[step]) return;
    const update = () => {
      const el = visibleMatch(steps[step].selector);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, step, steps]);

  const finish = async () => {
    setOpen(false);
    try {
      await api.put("/client/onboarding/progress", { tour_seen: true });
      setUser((u) => (u ? { ...u, onboarding: { ...u.onboarding, tour_seen: true } } : u));
    } catch (e) {
      // non-bloquant : la visite reste "vue" localement pour cette session
    }
  };

  if (!open || !steps[step]) return null;

  const s = steps[step];
  const isLast = step === steps.length - 1;
  const panelStyle = rect
    ? { top: Math.min(rect.bottom + 12, window.innerHeight - 200), left: Math.min(Math.max(rect.left, 12), window.innerWidth - 336) }
    : { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Visite guidée du portail">
      <div className="absolute inset-0 bg-black/40" onClick={finish} aria-hidden="true" />
      {rect && (
        <div
          aria-hidden="true"
          className="fixed border-2 border-ambre-vif rounded-sm pointer-events-none transition-all duration-300"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
        />
      )}
      <motion.div
        ref={panelRef}
        data-testid="guided-tour-panel"
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.2 }}
        className="fixed bg-white rounded-sm shadow-xl p-5 w-80 max-w-[90vw]"
        style={panelStyle}
      >
        <div className="text-xs text-marine/80 mb-1">{step + 1} / {steps.length}</div>
        <h2 className="font-display text-lg text-marine mb-1.5">{s.title}</h2>
        <p className="text-sm text-marine mb-4">{s.body}</p>
        <div className="flex items-center justify-between">
          <button type="button" onClick={finish} data-testid="tour-quit" className="text-xs text-marine/80 hover:text-marine">
            Quitter
          </button>
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep((i) => i + 1))}
            data-testid="tour-next"
            className="text-xs bg-marine text-white rounded-sm px-4 py-2 hover:bg-marine-hover transition-colors"
          >
            {isLast ? "Terminer" : "Suivant"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
